<template>
  <div>
    <div
      v-if="withDetails"
      class="sticky top-0 z-10 bg-gray-100"
    >
      <AsteonlineCard>
        <CardBody>
          <div class="flex">
            <div class="w-1/3">
              <AuctionDetails :auction="auction" />
            </div>

            <div class="flex-grow">
              <AuctionExpirationDetails :auction="auction" />
            </div>

            <div class="flex flex-col ml-4">
              <AuctionBadge :auction="auction" />
              <div>
                <select
                  v-model="viewMode"
                  class="p-1 bg-white border mr-2 hidden"
                >
                  <option :value="'compact'">
                    {{ $t("common.compact_view") }}
                  </option>
                  <option :value="'full'">
                    {{ $t("common.full_view") }}
                  </option>
                </select>
                <select
                  v-model="onlyBookmark"
                  class="p-1 bg-white border w-full"
                >
                  <option :value="false">
                    {{ $t("common.filters_all_lots") }}
                  </option>
                  <option :value="true">
                    {{ $t("common.filters_only_bookmarked_lots") }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </CardBody>
      </AsteonlineCard>

      <AsteonlineCard>
        <CardBody>
          <div class="flex flex-wrap">
            <div
              v-for="(quantity, productUuid) in auction.quantitiesPerProduct"
              :key="'auction-full-item-' + auction.uuid + '-' + productUuid"
              class="mb-2 w-1/2 md:w-1/4 p-2"
              :style="{ 'background-color': productToColor(productMap[productUuid].code) }"
            >
              <div class="flex items-center">
                <div>
                  <h4 class="font-bold underline">
                    {{ $t("product.product") }} {{ JSON.parse(productMap[productUuid].multilangName)[$i18n.locale] }}
                  </h4>
                </div>
                <div v-if="authStore.canBid && auction.me">
                  <div class="h-[30px] ml-4">
                    <LotProductPie
                      :winning="auction.me.products[productUuid].winningQuantity"
                      :biddable="auction.me.products[productUuid].biddableQuantity"
                    />
                  </div>
                </div>
                <div v-if="authStore.canBid && auction.me && auction.me.groupUser">
                  <div class="h-[30px]  ml-4">
                    <LotProductPie
                      :winning="auction.me.products[productUuid].winningQuantity"
                      :biddable="auction.me.products[productUuid].biddableQuantity"
                    />
                  </div>
                </div>
              </div>
              <div>
                <div>
                  {{ $t('auction.total_quantity') }}: <b>{{ auction.quantitiesPerProduct[productUuid] }}t</b>
                </div>
                <div v-if="auction.qmgs">
                  {{ $t('auction.min_quantity') }}: <b>{{ auction.qmgs[productUuid] }}t</b>
                </div>
                <div v-if="auction.redistributePerProduct[productUuid]">
                  {{ $t('auction.redistributable_quantity') }}: <b>{{ auction.redistributePerProduct[productUuid]
                  }}t</b>
                </div>
                <div>
                  {{ $t('auction.lot_count') }}: <b>{{ auction.countLotsPerProduct[productUuid] }}</b>
                </div>
                <div
                  v-if="authStore.canBid && auction.me"
                  class="flex flex-wrap"
                >
                  <div>
                    {{ $t("auction.you_are_winning") }}: <b>{{ auction.me.products[productUuid].winningQuantity }}t</b>
                  </div>
                  <span v-if="auction.me.groupUser">
                    &nbsp;({{ $t("auction.as_group") }}: <b>{{ auction.me.products[productUuid].winningQuantityGroup
                    }}t</b>)
                  </span>
                </div>
                <div
                  v-if="authStore.canBid && auction.me"
                  class="flex flex-wrap"
                >
                  <div>
                    {{ $t("auction.you_can_bid_on") }}: <b>{{ auction.me.products[productUuid].biddableQuantity }}t</b>
                  </div>
                  <span v-if="auction.me.groupUser">
                    &nbsp;({{ $t("auction.as_group") }}: <b>{{ auction.me.products[productUuid].biddableQuantityGroup
                    }}t</b>)
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="text-right text-xs">
            <a
              href="#"
              @click.prevent="withDetails = false"
            >
              <ChevronUpIcon class="h-5 w-5 inline-block cursor-pointer" />
              Nascondi dettagli
            </a>
          </div>
        </CardBody>
      </AsteonlineCard>
    </div>

    <AsteonlineCard
      v-if="!withDetails"
      class="sticky top-0 z-10"
    >
      <CardBody>
        <div class="text-right text-xs">
          <a
            href="#"
            @click.prevent="withDetails = true"
          >
            <ChevronDownIcon class="h-5 w-5 inline-block cursor-pointer" />
            Mostra dettagli
          </a>
        </div>
      </CardBody>
    </AsteonlineCard>

    <LotFullList
      v-if="viewMode == 'full'"
      :auction="auction"
      :product-map="productMap"
      :only-bookmark="onlyBookmark"
      :outbid-lot-uuid="outbidLotUuid"
      @update-auction="$emit('update-auction', $event)"
    />

    <LotCompactList
      v-if="viewMode == 'compact'"
      :auction="auction"
      :product-map="productMap"
      :only-bookmark="onlyBookmark"
      :outbid-lot-uuid="outbidLotUuid"
      @update-auction="$emit('update-auction', $event)"
    />
  </div>
</template>

<script setup>
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/solid';
import { computed, ref } from 'vue';

import AuctionBadge from '@/components/auction/AuctionBadge.vue';
import AuctionDetails from '@/components/auction/AuctionDetails.vue';
import AuctionExpirationDetails from '@/components/auction/AuctionExpirationDetails.vue';
import AsteonlineCard from '@/components/card/AsteonlineCard.vue';
import CardBody from '@/components/card/CardBody.vue';
import LotProductPie from '@/components/charts/LotProductPie.vue';
import LotCompactList from '@/components/lot/LotCompactList.vue';
import productToColor from '@/services/product-to-color';
import { useAuthStore } from '@/stores/auth';

import LotFullList from './lot/LotFullList.vue';

const emit = defineEmits(['update-auction'])
const props = defineProps({
  auction: {
    type: Object,
    required: true
  },
  outbidLotUuid: {
    type: String,
    required: false,
    default: null
  }
})

const authStore = useAuthStore();

const productMap = computed(() => props.auction.category.products.reduce((map, product) => {
  map[product.uuid] = product;
  return map
}, {}))

const onlyBookmark = ref(false);
const withDetails = ref(true);
const viewMode = ref('compact');

</script>